import { MenuButton as ChakraMenuButton, MenuButtonProps as ChakraMenuButtonProps } from '@chakra-ui/react';
import { useMultiStyleConfig, forwardRef } from '@chakra-ui/system';
import { mergeWith } from '@chakra-ui/utils';
import PropTypes from 'prop-types';
import React from 'react';

import DisplayNames from './enums';

import { getChild } from '_helpers';
import Select, { SelectProps } from 'atoms/Select/Select';
import COLORS from 'theme/colors';

export type MenuButtonProps = ChakraMenuButtonProps &
  Pick<SelectProps, 'size' | 'variant'> & {
    isDisabled?: boolean;
    hideChevron?: boolean;
  };

type SelectSizes = 'xs' | 'sm' | 'md' | 'lg';
type SelectVariants = 'basic' | 'outline' | 'ghost' | 'unstyled';

const selectSizes: SelectSizes[] = ['xs', 'sm', 'md', 'lg'];
const selectVariants: SelectVariants[] = ['basic', 'outline', 'ghost', 'unstyled'];

export const propTypes = {
  size: PropTypes.oneOf(selectSizes),
  variant: PropTypes.oneOf(selectVariants),
  isDisabled: PropTypes.bool,
  hideChevron: PropTypes.bool
};
export const defaultProps = {
  size: 'md',
  variant: 'basic',
  hideChevron: false,
  iconColor: COLORS.black.lighter
};

const MenuButton = forwardRef<MenuButtonProps, 'button'>((props: MenuButtonProps & typeof defaultProps, ref) => {
  const { hideChevron, sx, ...rest } = props;
  const { iconColor, ...buttonOnlyProps } = rest;

  const { field: selectStyles } = useMultiStyleConfig('Select', props);
  const { button: menuButtonStyleOverrides } = useMultiStyleConfig('Menu', props);
  const sxStyles = {
    ...selectStyles,
    ...menuButtonStyleOverrides
  };
  const hideChevronSxStyles = {
    ...sxStyles,
    py: selectStyles.ps,
    px: selectStyles.ps,
    ps: selectStyles.ps,
    pe: selectStyles.ps,
    width: 'fit-content'
  };

  const buttonWithIconSxStyles = {
    ...sxStyles,
    p: 'initial',
    w: selectStyles.h,
    h: selectStyles.h,
    lineHeight: 0
  };

  const hasIconChildren = !!getChild('Icon', props.children);
  if (props.variant === 'unstyled') {
    return <ChakraMenuButton ref={ref} {...buttonOnlyProps} />;
  }

  if (hideChevron && hasIconChildren) {
    return (
      <ChakraMenuButton
        disabled={buttonOnlyProps.isDisabled}
        ref={ref}
        sx={mergeWith(buttonWithIconSxStyles, sx)}
        {...buttonOnlyProps}
      />
    );
  }

  if (hideChevron) {
    return <ChakraMenuButton ref={ref} sx={mergeWith(hideChevronSxStyles, sx)} {...buttonOnlyProps} />;
  }

  return <Select as={ChakraMenuButton} ref={ref} sx={mergeWith(sxStyles, sx)} {...(rest as SelectProps)} />;
});

MenuButton.propTypes = propTypes;
MenuButton.defaultProps = defaultProps;
MenuButton.displayName = DisplayNames.MenuButton;

export default MenuButton;
